import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { Input } from 'antd';
import type { ChangeEvent, KeyboardEvent } from 'react';

import { useDebouncedCallback } from '@/hooks/use-debounced-callback';

import styles from './search-bar.module.scss';

export type SearchBarProps = {
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  /** Debounced while typing; fires immediately on Enter and clear. */
  onSearch: (term: string) => void;
  debounceMs?: number;
  /** Terms shorter than this emit as empty (full list). Default 1 keeps every typed char firing. */
  minChars?: number;
  /** Hard cap on the input length, forwarded to the underlying antd Input. */
  maxLength?: number;
  /** Fill the container instead of the default 300px width. */
  block?: boolean;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
};

export function SearchBar({
  value,
  defaultValue,
  placeholder = 'Search',
  onSearch,
  debounceMs = 300,
  minChars = 1,
  maxLength,
  block,
  disabled,
  className,
  'aria-label': ariaLabel = 'Search',
}: SearchBarProps) {
  // One gate for typing, Enter, and clear: sub-threshold terms behave as empty (full list).
  const emit = (term: string) => onSearch(term.length < minChars ? '' : term);
  const debounced = useDebouncedCallback(emit, debounceMs);

  const fireNow = (term: string) => {
    debounced.cancel();
    emit(term);
  };

  return (
    <Input
      type="search"
      role="searchbox"
      value={value}
      defaultValue={defaultValue}
      placeholder={placeholder}
      disabled={disabled}
      maxLength={maxLength}
      allowClear
      aria-label={ariaLabel}
      prefix={<MagnifyingGlassIcon size={16} aria-hidden className={styles.icon} />}
      className={[styles.search, block ? styles.block : undefined, className]
        .filter(Boolean)
        .join(' ')}
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        // antd's clear button emits a synthetic click-typed change with value ''
        if (event.type === 'click' && event.target.value === '') fireNow('');
        else debounced(event.target.value);
      }}
      onPressEnter={(event: KeyboardEvent<HTMLInputElement>) =>
        fireNow((event.target as HTMLInputElement).value)
      }
    />
  );
}
